import React from 'react';
import { RSInput, RSButton, RSFormLabel } from 'reactsymbols-kit';
import { observer } from 'mobx-react';
import { useInspectorChange, useController } from '../../hooks';
import { Row, Col } from '../Grid';
import EditStateController from '../../merode/controllers/EditStateController';
import Styles from '../../styles/colors';
import type { IState } from '../../stores/model/metamodel/state';
import type { IStateInspector } from '../../stores/ui/state_inspector';
import type { IModelVersion } from '../../merode/controllers/Controller';
import type Report from '../../merode/checks/Report';
import { asRSChange, rsValue, type RSInputChange } from './rsForm';

/** Props for the buttons at the bottom of an editable inspector. */
interface InspectorButtonsProps {
  /** Are there pending changes? */
  dirty: boolean;
  /** Callback to apply pending changes. */
  onApply: () => void;
  /** Callback to discard pending changes. */
  onDiscard: () => void;
  /** Callback to delete the element. */
  onDelete: () => void;
}

/**
 * Utility component for the buttons at the bottom of the state inspector.
 *
 * Props:
 * - dirty: are there pending changes?
 * - onApply: callback to apply pending changes
 * - onDiscard: callback to discard pending changes
 * - onDelete: callback to delete the state
 */
const StateInspectorButtons = ({ dirty, onApply, onDiscard, onDelete }: InspectorButtonsProps) => {
  if (dirty) {
    return (
      <div className="edit-state p-[15px]">
        <Row gutter={5}>
          <Col md={6}>
            <RSButton
              type="button"
              background={Styles.defaultGradient}
              color={Styles.darkTextColor}
              value="Discard"
              onClick={onDiscard}
            />
          </Col>
          <Col md={6}>
            <RSButton
              type="submit"
              background={Styles.blueGradient}
              value="Apply changes"
              onClick={onApply}
            />
          </Col>
        </Row>
      </div>
    );
  } else {
    return (
      <div className="remove-state p-[15px]">
        <RSButton
          type="button"
          background={Styles.redGradient}
          value="Remove state"
          onClick={onDelete}
        />
      </div>
    );
  }
};

/**
 * State inspector.
 *
 * Props:
 * - state: the state
 * - ui: the StateInspector ui store
 * - version: The ModelVersion (to pass to the controller)
 * - onReport: callback to call when a report is generated
 * - requestClose: callback to close the inspector
 */
interface StateInspectorProps {
  /** The state to inspect. */
  state: IState;
  /** The StateInspector ui store. */
  ui: IStateInspector;
  /** The ModelVersion (to pass to the controller). */
  version: IModelVersion;
  /** Callback to close the inspector. */
  requestClose?: () => void;
  /** Callback to call when a report is generated. */
  onReport?: (report: Report) => void;
}

const StateInspector = ({ state, ui, version, requestClose, onReport }: StateInspectorProps) => {
  // `useInspectorChange` reads/writes the change value by key on `state`; type
  // its element param as the string-record surface it indexes (the inspector
  // edits only `name`). Same node at runtime — narrowing cast only.
  const [name, onChangeName, nameDirty] = useInspectorChange<string | null>(
    ui,
    state as unknown as Record<string, string | null>,
    'name'
  );
  const controller = useController(
    () => new EditStateController(version, state.fsm.objectType.id, state.fsm.id)
  );
  // `checkNameInput`/`editState` are typed for `string`, but the pending-change
  // value is `string | null`; both handle `null` at runtime (the controller via
  // `isEmpty`, the command layer downstream). Cast at these boundaries — runtime
  // value, including `null`, is unchanged.
  const nameCheck = controller.checkNameInput(name as string);
  const nameOK = nameCheck.ok;

  // Handler to apply the pending changes
  const onApply = async () => {
    ui.setProjectBusy(true);
    const report = await controller.editState(state.id, name as string);
    if (report.successful) {
      ui.discardChanges();
    }
    ui.setProjectBusy(false);
    if (onReport != null) {
      onReport(report);
    }
  };

  // Handler to delete the state
  const onDelete = async () => {
    ui.setProjectBusy(true);
    const report = await controller.deleteState(state.id);
    if (requestClose != null) {
      requestClose();
    }
    ui.setProjectBusy(false);
    if (onReport != null) {
      onReport(report);
    }
  };

  return (
    <div className="state-inspector flex h-full flex-col">
      <div className="state-content flex-1 overflow-auto px-[15px] py-5">
        <RSFormLabel>Object</RSFormLabel>
        <div>{state.fsm.objectType.name}</div>

        <RSFormLabel>FSM</RSFormLabel>
        <div>{state.fsm.name}</div>

        <RSInput
          label="State name"
          value={rsValue(name)}
          onChange={asRSChange<RSInputChange>(onChangeName)}
          className={nameDirty ? 'dirty' : ''}
          level={rsValue(nameOK ? null : 'danger')}
          text={rsValue(nameOK ? null : nameCheck.message)}
          disabled={state.fsm.type === 'DEFAULT'}
        />

        <RSFormLabel>Type</RSFormLabel>
        {state.type.replace(/_/g, ' ').toLowerCase()}
      </div>
      {state.fsm.type !== 'DEFAULT' && (
        <StateInspectorButtons
          dirty={ui.dirty}
          onApply={onApply}
          onDiscard={ui.discardChanges}
          onDelete={onDelete}
        />
      )}
    </div>
  );
};

// Wrap the view in observer
const ObservedStateInspector = observer(StateInspector);
export default ObservedStateInspector;
